import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  InputAdornment,
  TablePagination
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

const SchoolsTable = ({ 
  schools = [], 
  teamId, 
  onRemoveSchool, 
  maxHeight = 400,
  showPagination = true 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter schools based on search query
  const filteredSchools = useMemo(() => {
    if (!searchQuery.trim()) return schools;
    
    const query = searchQuery.toLowerCase().trim();
    return schools.filter(school => 
      (school.name && school.name.toLowerCase().includes(query)) ||
      (school.uuid && school.uuid.toLowerCase().includes(query)) ||
      (school.ncessch && school.ncessch.toLowerCase().includes(query))
    );
  }, [schools, searchQuery]);

  // Paginated schools
  const paginatedSchools = useMemo(() => {
    if (!showPagination) return filteredSchools;
    
    const startIndex = page * rowsPerPage;
    return filteredSchools.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredSchools, page, rowsPerPage, showPagination]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  if (!schools || schools.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No schools assigned to this team
      </Typography>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search schools by name, UUID, or NCES ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          {filteredSchools.length} of {schools.length} schools
          {searchQuery && ` matching "${searchQuery}"`}
        </Typography>
      </Box>

      {/* Schools Table */}
      <TableContainer 
        component={Paper} 
        variant="outlined"
        sx={{ 
          maxHeight: maxHeight,
          '& .MuiTableCell-root': {
            borderBottom: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>
                School Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', width: 120 }}>
                UUID
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', width: 120 }}>
                NCES ID
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', width: 80 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSchools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {searchQuery ? 'No schools match your search' : 'No schools found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedSchools.map((school, index) => (
                <TableRow 
                  key={school.uuid || index}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:nth-of-type(even)': { bgcolor: 'grey.50' }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {school.name || 'Unknown School'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      >
                        {school.uuid ? `${school.uuid.substring(0, 8)}...` : '—'}
                      </Typography>
                      {school.uuid && (
                        <Tooltip title="Copy UUID">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyToClipboard(school.uuid)}
                            sx={{ p: 0.25 }}
                          >
                            <CopyIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {school.ncessch ? (
                        <>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.75rem',
                              color: 'text.secondary'
                            }}
                          >
                            {school.ncessch}
                          </Typography>
                          <Tooltip title="Copy NCES ID">
                            <IconButton 
                              size="small" 
                              onClick={() => handleCopyToClipboard(school.ncessch)}
                              sx={{ p: 0.25 }}
                            >
                              <CopyIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Chip 
                          label="No NCES" 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Remove school from team">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onRemoveSchool(teamId, school.uuid)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {showPagination && filteredSchools.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={filteredSchools.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ 
            borderTop: 1, 
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}
        />
      )}
    </Box>
  );
};

export default SchoolsTable; 